import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { NumericBox } from "../components/common/NumericBox";
import { SelectBox } from "../components/common/SelectBox";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PopupContent from "../components/popupContent";
import { FormatString } from "../helpers/stringHelpers";
import TranslationContext from "../providers/TranslationContext";
import { ValidationProvider } from "../providers/ValidationContext";
import { addNotification } from "../redux/actions";
import { apiCreateOrUpdateHardener, apiGetHardenerDetail } from "../services/HardenerService";
import { validateForm } from "../validation/formValidation";
import { model } from "../validation/models/hardener";
import PopupOperationsBar from "./PopupOperationsBar";

// styles
require("./PopupGeneralStyles.css");

interface Props {
    id?: number;
    handleCancel: any;
    refreshData: any;
    discountGroups: any[];
}

const HardenerPopup = (props: Props) => {
    const translationContext = useContext(TranslationContext) as any;
    const prefix = translationContext.prefix;
    const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
    const cloneDeep = require('clone-deep');
    const [validationModel, setValidationModel] = useState(cloneDeep(model));

    const [loading, setLoading] = useState(true);
    const data: any = {};
    const [formData, setFormData] = useState(data);
    const [saveError, setSaveError] = useState(false);

    const isEditMode = props.id ? true : false;

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.id) {
            apiGetHardenerDetail(props.id)
                .then((responseData) => {
                    setFormData(responseData.data);
                    setLoading(false);
                })
                .catch((err: any) => {
                    dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
                    props.handleCancel();
                });
        }
    }, []);

    const onAddingClick = () => {
        validateForm(validationModel, formData, isEditMode);
        setValidationModel({ ...validationModel });
        if (validationModel.isValid) {
            setLoading(true);
            apiCreateOrUpdateHardener(formData)
                .then((responseData) => {
                    dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.shortName), type: 'success' }));
                    props.refreshData();
                    props.handleCancel();

                    setLoading(false);
                })
                .catch((err: any) => {
                    dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.shortName), type: 'error' }));
                    props.handleCancel();

                    setSaveError(true);
                });
        }
    };

    const onEditingClick = () => {
        validateForm(validationModel, formData, isEditMode);
        setValidationModel({ ...validationModel });
        if (validationModel.isValid) {
            setLoading(true);
            apiCreateOrUpdateHardener(formData)
                .then((responseData) => {
                    dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.shortName), type: 'success' }));
                    props.refreshData();
                    props.handleCancel();

                    setLoading(false);
                })
                .catch((err: any) => {
                    dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.shortName), type: 'error' }));
                    setSaveError(true);
                });
        }
    };

    const onCancelClick = () => {
        props.handleCancel();
    };

    return (
        <PopupContent header={props.id ? t('EditLabel') : t('CreateLabel')}>
            <ValidationProvider value={validationModel}>
                <TextBox
                    dataField="name"
                    value={formData.name}
                    formData={formData}
                    setStateMethod={setFormData}
                    id="name"
                    label={t('Labels.name')}
                    placeholder={t('Labels.namePlaceholder')}
                    disabled={false}
                />
                <TextBox
                    dataField="shortName"
                    value={formData.shortName}
                    formData={formData}
                    setStateMethod={setFormData}
                    id="shortName"
                    label={t('Labels.shortName')}
                    placeholder={t('Labels.shortNamePlaceholder')}
                    disabled={false}
                />
                <NumericBox
                    dataField="size"
                    defaultValue={formData.size}
                    formData={formData}
                    setStateMethod={setFormData}
                    id="size"
                    label={t('Labels.size')}
                    decimalPlaces={2}
                />
                <NumericBox
                    dataField="density"
                    defaultValue={formData.density}
                    formData={formData}
                    setStateMethod={setFormData}
                    id="density"
                    label={t('Labels.density')}
                    decimalPlaces={2}
                />
                <NumericBox
                    dataField="price"
                    defaultValue={formData.price}
                    formData={formData}
                    setStateMethod={setFormData}
                    id="price"
                    label={t('Labels.price')}
                    decimalPlaces={2}
                />
                {
                    /*                
                    <NumericBox
                        dataField="margin"
                        defaultValue={formData.margin}
                        formData={formData}
                        setStateMethod={setFormData}
                        id="margin"
                        label={t('Labels.margin')}
                        decimalPlaces={2}
                    />
                    */
                }
                <SelectBox
                    dataField="discountGroupId"
                    value={formData.discountGroupId}
                    formData={formData}
                    label={t('Labels.discountGroup')}
                    options={props.discountGroups}
                    placeholder={t('Labels.discountGroupPlaceholder')}
                    setStateMethod={setFormData}
                    isDisabled={false}
                />

                <TextArea
                    id="noteInternal"
                    label={t('Labels.note')}
                    placeholder={t('Labels.notePlaceholder')}
                    rows={4}
                    dataField="noteInternal"
                    value={formData.noteInternal}
                    formData={formData}
                    setStateMethod={setFormData}
                />

                <PopupOperationsBar
                    handleAdding={onAddingClick}
                    handleCancellation={onCancelClick}
                    handleEditing={onEditingClick}
                    id={props.id}
                />
            </ValidationProvider>
        </PopupContent>
    );
};

export default HardenerPopup;